import React, { useLayoutEffect, useRef, useState } from "react";
import { buildLedgerGroups } from "./ledgerGrouping";

// A booking-node diagram, not a row-per-ledger-line table. Debts sharing the
// same origin booking (e.g. a debt split into a partial adjustment + a
// leftover that gets adjusted separately) are grouped under ONE origin node,
// branching out to each booking it actually got applied to — never repeating
// the origin box per branch. Each branch shows the amount that specific
// adjustment moved (not the original debt's total), plus how/when it was paid.
const Node = React.forwardRef(({ refNo, clientName, tone, debtDetail, paidAmount }, ref) => (
  <div
    ref={ref}
    className={`px-3 py-2 rounded-lg border text-sm text-center bg-white shadow-sm ${
      debtDetail ? "min-w-[220px] max-w-[260px]" : "min-w-[150px]"
    } ${
      tone === "debt"
        ? "border-red-300 text-red-700 bg-red-50"
        : tone === "pending"
          ? "border-gray-300 text-gray-500 border-dashed bg-gray-50"
          : "border-green-300 text-green-700 bg-green-50"
    }`}
  >
    <div className="font-semibold">{refNo || "N/A"}</div>
    {clientName && <div className="text-xs text-gray-500">{clientName}</div>}
    {paidAmount != null && (
      <div className="text-[11px] mt-1 pt-1 border-t border-green-200 font-semibold">
        ✓ Debt Paid: ₹{paidAmount}
      </div>
    )}
    {debtDetail && (
      <div className="text-[11px] mt-1.5 pt-1.5 border-t border-red-200 text-left space-y-0.5">
        <div>
          Debt raised: <span className="font-semibold">₹{debtDetail.originalDebt}</span>
        </div>
        <div className="text-green-700">
          Paid off: <span className="font-semibold">₹{debtDetail.usedAmount}</span>
        </div>
        <div className={debtDetail.outstandingAmount > 0 ? "text-red-700" : "text-green-700"}>
          Outstanding: <span className="font-semibold">₹{debtDetail.outstandingAmount}</span>
        </div>
        {debtDetail.outstandingAmount <= 0 && (
          <div className="text-green-700 font-semibold">✓ Closed</div>
        )}
        {debtDetail.origins?.length > 0 && (
          <div className="mt-1 pt-1 border-t border-red-200 space-y-1">
            {debtDetail.origins.map((o, idx) => (
              <div key={idx}>
                <span className="text-gray-500">{o.date}</span> — {o.description}
                <span className="font-semibold"> (₹{o.amount})</span>
                {o.remark && <div className="text-gray-500">— {o.remark}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </div>
));

// Chronological, row-per-movement audit trail for one debt chain: every debt
// raised, every leftover carried forward, and every settlement, in the order
// they actually happened — so a split like ₹3000 → ₹1200 + ₹1800 can be
// traced event by event instead of only seeing the final summary numbers.
const TransactionTrail = ({ group }) => {
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");

  const paymentOptions = Array.from(
    new Set(group.trail.map((t) => t.paymentType).filter(Boolean)),
  );

  const filtered = group.trail.filter((t) => {
    if (paymentFilter !== "all" && t.paymentType !== paymentFilter) return false;
    if (eventFilter !== "all" && t.eventType !== eventFilter) return false;
    return true;
  });

  const eventTagStyle = {
    "Debt Raised": "bg-red-100 text-red-700",
    "Leftover Carried": "bg-gray-100 text-gray-600",
    Settled: "bg-green-100 text-green-700",
  };

  return (
    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs">
        <span className="text-gray-500 font-semibold">Filter:</span>
        <select
          className="border border-gray-200 rounded px-2 py-1 text-xs"
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
        >
          <option value="all">All events</option>
          <option value="Debt Raised">Debt Raised</option>
          <option value="Leftover Carried">Leftover Carried</option>
          <option value="Settled">Settled</option>
        </select>
        <select
          className="border border-gray-200 rounded px-2 py-1 text-xs"
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
        >
          <option value="all">All payment methods</option>
          {paymentOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-left">
              <th className="px-3 py-2 whitespace-nowrap">Date &amp; Time</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">From → To</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Payment Method</th>
              <th className="px-3 py-2">Purpose / Reason</th>
              <th className="px-3 py-2 whitespace-nowrap">Outstanding After</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="px-3 py-2 whitespace-nowrap text-gray-500">{t.date}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${eventTagStyle[t.eventType]}`}>
                    {t.eventType}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{t.from} → {t.to}</td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">₹{t.amount}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {t.paymentType ? (
                    <>
                      {t.paidVia ? `${t.paidVia} · ` : ""}
                      {t.paymentType}
                      {t.utr ? ` · UTR ${t.utr}` : ""}
                    </>
                  ) : (
                    <span className="text-amber-600">not on record</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {t.description}
                  {t.remark && <div className="text-gray-400">— {t.remark}</div>}
                </td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">
                  <span className={t.outstandingAfter > 0 ? "text-red-600" : "text-green-600"}>
                    ₹{t.outstandingAfter}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-gray-400">
                  No movements match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Renders one origin booking + its branches as a real flow diagram: SVG
// bezier lines measured from the actual DOM node edges, so the line always
// touches both boxes exactly (Figma/react-flow style), instead of a fixed
// arrow glyph floating in a gap.
const GroupFlow = ({ group }) => {
  const containerRef = useRef(null);
  const originRef = useRef(null);
  const branchRefs = useRef([]);
  const [edges, setEdges] = useState([]);
  const [showTrail, setShowTrail] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const containerEl = containerRef.current;
      const originEl = originRef.current;
      if (!containerEl || !originEl) return;
      const containerBox = containerEl.getBoundingClientRect();
      const originBox = originEl.getBoundingClientRect();
      const start = {
        x: originBox.right - containerBox.left,
        y: originBox.top + originBox.height / 2 - containerBox.top,
      };
      const next = branchRefs.current.map((el) => {
        if (!el) return null;
        const box = el.getBoundingClientRect();
        return {
          x1: start.x,
          y1: start.y,
          x2: box.left - containerBox.left,
          y2: box.top + box.height / 2 - containerBox.top,
        };
      });
      setEdges(next);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [group]);

  return (
    <div className="flex flex-col">
    <div ref={containerRef} className="relative flex items-stretch gap-20 py-2">
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }}>
        <defs>
          <marker id="flow-arrow-resolved" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#22c55e" />
          </marker>
          <marker id="flow-arrow-pending" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#9ca3af" />
          </marker>
        </defs>
        {edges.map((e, i) => {
          if (!e) return null;
          const debt = group.branches[i];
          const resolved = debt.is_resolved;
          // Capped at half the actual gap so the two control points never cross
          // past each other — an uncapped/oversized dx made the curve overshoot
          // its endpoint and hook back on itself when nodes sat close together.
          const gap = e.x2 - e.x1;
          const dx = Math.min(60, Math.max(gap / 2, 10));
          const path = `M ${e.x1} ${e.y1} C ${e.x1 + dx} ${e.y1}, ${e.x2 - dx} ${e.y2}, ${e.x2} ${e.y2}`;
          return (
            <path
              key={debt.id}
              d={path}
              fill="none"
              stroke={resolved ? "#22c55e" : "#9ca3af"}
              strokeWidth="2"
              strokeDasharray={resolved ? "0" : "4 4"}
              markerEnd={resolved ? "url(#flow-arrow-resolved)" : "url(#flow-arrow-pending)"}
            />
          );
        })}
      </svg>

      <div className="flex items-center" style={{ zIndex: 1 }}>
        <Node
          ref={originRef}
          refNo={group.ref_no}
          clientName={group.client_name}
          tone="debt"
          debtDetail={{
            originalDebt: group.originalDebt,
            usedAmount: group.usedAmount,
            outstandingAmount: group.outstandingAmount,
            origins: group.origins,
          }}
        />
      </div>

      <div className="flex-1 flex flex-col justify-around gap-6" style={{ zIndex: 1 }}>
        {group.branches.map((debt, i) => {
          const resolved = debt.is_resolved;
          const to = debt.resolved_in;
          // The amount THIS branch actually moved — the resolved amount when
          // it was applied somewhere, not the origin debt's total (that
          // total is only correct for a still-unresolved branch).
          const branchAmount = resolved ? to?.amount : Math.abs(debt.amount);
          return (
            <div key={debt.id} className="flex items-center gap-4">
              <div ref={(el) => (branchRefs.current[i] = el)}>
                {resolved ? (
                  <Node
                    refNo={to?.ref_no}
                    clientName={to?.client_name}
                    tone="resolved"
                    paidAmount={branchAmount}
                  />
                ) : (
                  <Node refNo="Outstanding" tone="pending" />
                )}
              </div>
              <div className="text-xs whitespace-nowrap">
                <div className="font-semibold text-gray-700">
                  ₹{branchAmount} {resolved ? "paid" : "still due"}
                </div>
                {resolved && (
                  <div className="text-gray-400">
                    on {to?.date || debt.date}
                    {to?.paid_via ? ` · via ${to.paid_via}` : ""}
                    {to?.payment_type ? ` · ${to.payment_type}` : ""}
                    {to?.utr ? ` · UTR ${to.utr}` : ""}
                    {!to?.payment_type && !to?.utr && (
                      <span className="text-amber-600"> · payment method not on record</span>
                    )}
                  </div>
                )}
                {!resolved && <div className="text-gray-400">since {debt.date}</div>}
                {resolved && to?.remark && <div className="text-gray-400">— {to.remark}</div>}
                {!resolved && debt.remark && <div className="text-gray-400">— {debt.remark}</div>}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      <button
        type="button"
        onClick={() => setShowTrail((v) => !v)}
        className="mt-2 text-xs font-semibold text-green-700 hover:underline self-start"
      >
        {showTrail ? "Hide Full Trail ▲" : "View Full Trail ▼"}
      </button>
      {showTrail && <TransactionTrail group={group} />}
    </div>
  );
};

const VendorLedgerPipeline = ({ entries }) => {
  const debtEntries = entries.filter((e) =>
    ["trip_cancelled", "bill_overpaid"].includes(e.type),
  );

  if (debtEntries.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-8">
        No debts/adjustments recorded for this vendor.
      </p>
    );
  }

  const groups = buildLedgerGroups(entries);

  return (
    <div className="flex flex-col gap-8 py-4">
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
        <span className="font-semibold text-gray-600 dark:text-gray-300">How to read this:</span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-red-300 bg-red-50" /> Origin booking that raised the debt
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-green-300 bg-green-50" /> Booking the debt was settled against
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-dashed border-gray-300 bg-gray-50" /> Still outstanding, not yet settled
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#22c55e" strokeWidth="2" /></svg> Settled path
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#9ca3af" strokeWidth="2" strokeDasharray="4 4" /></svg> Pending path
        </span>
      </div>
      {groups.map((group) => {
        const isClosed = group.outstandingAmount <= 0;
        return (
          <div key={group.ref_no} className="flex flex-col gap-2">
            <div className="flex items-center gap-4 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 w-fit">
              <span>
                <span className="text-gray-500">Original Debt:</span>{" "}
                <span className="font-semibold">₹{group.originalDebt}</span>
              </span>
              <span>
                <span className="text-gray-500">Used:</span>{" "}
                <span className="font-semibold text-green-600">₹{group.usedAmount}</span>
              </span>
              <span>
                <span className="text-gray-500">Outstanding:</span>{" "}
                <span className={`font-semibold ${isClosed ? "text-green-600" : "text-red-600"}`}>
                  ₹{group.outstandingAmount}
                </span>
              </span>
              {isClosed && (
                <span className="text-green-600 font-semibold">✓ Closed</span>
              )}
            </div>

            <GroupFlow group={group} />
          </div>
        );
      })}
    </div>
  );
};

export default VendorLedgerPipeline;
